import { TFunction } from "i18next";

import { DeviceType, ProductMappingItem } from "domain/licenses";
import { Authority } from "domain/roles";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";

const LAST_COMPLETED_STEP_INDEX_WITH_IBR = 4;
const LAST_COMPLETED_STEP_INDEX_WITHOUT_IBR = 3;

export const FEEDBACK_FORM_LINK = "https://forms.office.com/Pages/ResponsePage.aspx?id=";

export enum BmdeOnboardingSteps {
    STEP_WORKFLOW_TEMPLATE = "WORKFLOW_TEMPLATE",
    STEP_BOOTING_USB_DRIVE = "USB_DRIVE_CREATION",
    STEP_INSTALLING_BMDE = "INSTALLING_BMDE",
    STEP_RUNNING_ERASURE = "RUNNING_ERASURE",
    STEP_VIEWING_REPORT = "VIEWING_REPORT",
}

export enum BdeOnboardingSteps {
    STEP_WORKFLOW_TEMPLATE = "WORKFLOW_TEMPLATE",
    STEP_PRE_CONFIGURATION = "PRE_CONFIGURATION",
    STEP_BOOTING_USB_DRIVE = "USB_DRIVE_CREATION",
    STEP_RUNNING_ERASURE = "RUNNING_ERASURE",
    STEP_VIEWING_REPORT = "VIEWING_REPORT",
}

export enum BmpOpOnboardingSteps {
    STEP_SYSTEM_PREPARATION = "SYSTEM_PREPARATION",
    STEP_BMPOP_INSTALLATION = "BMPOP_INSTALLATION",
    STEP_LAUNCHING_BMPOP = "LAUNCHING_BMPOP",
}

export enum BarOnboardingSteps {
    STEP_SETUP_WINERASER = "SET_UP_BLANCCO_WINERASER",
    STEP_SETUP_SERVER_CONFIGURATION = "SET_UP_SERVER_CONFIGURATION",
    STEP_CREATE_CAPTURE_IMAGE = "CREATE_AND_CAPTURE_FIRST_IMAGE",
    STEP_REIMAGE_TARGET_MACHINE = "RE_IMAGE_TARGET_MACHINE",
    STEP_VIEW_REPORT = "VIEWING_REPORT",
}

// Blancco Mobile Workstation Edition uses the same step sequence as Blancco Drive Eraser.
export enum BmweOnboardingSteps {
    STEP_WORKFLOW_TEMPLATE = "WORKFLOW_TEMPLATE",
    STEP_INSTALLING_BMWE = "INSTALLING_BMWE",
    STEP_INITIAL_SETUP = "INITIAL_SETUP",
    STEP_RUNNING_ERASURE = "RUNNING_ERASURE",
    STEP_VIEWING_REPORT = "VIEWING_REPORT",
}

export const BMDE_ONBOARDING_STEPS = Object.values(BmdeOnboardingSteps);

export const BDE_ONBOARDING_STEPS = Object.values(BdeOnboardingSteps);

export const BMPOP_ONBOARDINGS_STEP = Object.values(BmpOpOnboardingSteps);

export const BAR_ONBOARDING_STEPS = Object.values(BarOnboardingSteps);

export const BMWE_ONBOARDING_STEPS = Object.values(BmweOnboardingSteps);

export function isStepCompleted(focusedStepIndex: number, lastCompletedStepIndex: number): boolean {
    return focusedStepIndex <= lastCompletedStepIndex;
}

export function getStepIndexForBde(step: BdeOnboardingSteps | undefined, hasIbr?: boolean) {
    if (!step) {
        return -1;
    }
    const availableSteps = hasIbr
        ? BDE_ONBOARDING_STEPS
        : BDE_ONBOARDING_STEPS.filter((s) => s !== BdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE);

    return availableSteps.indexOf(step);
}

export function getStepIndexForBmde(step: BmdeOnboardingSteps | undefined, hasIbr: boolean) {
    if (!step) {
        return -1;
    }

    const availableSteps = hasIbr
        ? BMDE_ONBOARDING_STEPS
        : BMDE_ONBOARDING_STEPS.filter((s) => s !== BmdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE);

    return availableSteps.indexOf(step);
}

export function getStepIndexForBmpOp(step: BmpOpOnboardingSteps | undefined) {
    if (!step) {
        return -1;
    }
    return BMPOP_ONBOARDINGS_STEP.indexOf(step);
}

export function getStepIndexForBar(step: BarOnboardingSteps | undefined) {
    if (!step) {
        return -1;
    }
    return BAR_ONBOARDING_STEPS.indexOf(step);
}

export function getStepIndexForBmwe(step: BmweOnboardingSteps | undefined, hasIbr?: boolean) {
    if (!step) {
        return -1;
    }
    const availableSteps = hasIbr
        ? BMWE_ONBOARDING_STEPS
        : BMWE_ONBOARDING_STEPS.filter((s) => s !== BmweOnboardingSteps.STEP_WORKFLOW_TEMPLATE);

    return availableSteps.indexOf(step);
}

export function getActiveStep(lastCompletedStepIndex: number): number {
    return lastCompletedStepIndex + 1;
}

export function isStepActive(lastCompletedStepIndex: number, currentStep: number): boolean {
    const activeStep = getActiveStep(lastCompletedStepIndex);
    return activeStep === currentStep;
}

export const mapCompletedStepToBmdeOnboardingStep = (lastCompletedStep: string, hasIbr: boolean) => {
    const steps = Object.values(BmdeOnboardingSteps);
    const availableSteps = hasIbr ? steps : steps.filter((step) => step !== BmdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE);
    return availableSteps.find((step) => step === lastCompletedStep);
};

export const mapCompletedStepToBdeOnboardingStep = (lastCompletedStep: string, hasIbr: boolean) => {
    const steps = Object.values(BdeOnboardingSteps);
    const availableSteps = hasIbr ? steps : steps.filter((step) => step != BdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE);
    return availableSteps.find((step) => step === lastCompletedStep);
};

export const mapCompletedStepToBmpOpOnboardingStep = (lastCompletedStep: string) => {
    const steps = Object.values(BmpOpOnboardingSteps);
    return steps.find((step) => step === lastCompletedStep);
};

export const mapCompletedStepToBarOnboardingStep = (lastCompletedStep: string) => {
    const steps = Object.values(BarOnboardingSteps);
    return steps.find((step) => step === lastCompletedStep);
};

export const mapCompletedStepToBmweOnboardingStep = (lastCompletedStep: string, hasIbr: boolean) => {
    const steps = Object.values(BmweOnboardingSteps);
    const availableSteps = hasIbr ? steps : steps.filter((step) => step !== BmweOnboardingSteps.STEP_WORKFLOW_TEMPLATE);
    return availableSteps.find((step) => step === lastCompletedStep);
};

export enum OnboardingProductType {
    BDE = "BDE",
    BMDE = "BMDE",
    BMPOP = "BMPOP",
    BAR = "BAR",
    BMWE = "BMWE",
}

export const OnboardingProductNames: Record<OnboardingProductType, string> = {
    [OnboardingProductType.BDE]: "Blancco Drive Eraser",
    [OnboardingProductType.BMDE]: "Mobile Diagnostics and Erasure",
    [OnboardingProductType.BMPOP]: "Blancco Management Portal On-Premise",
    [OnboardingProductType.BAR]: "Blancco Asset Reimaging",
    [OnboardingProductType.BMWE]: "Blancco Mobile Workspace Edition",
};

export const SUB_STEPS_INSTALL_BMDE = [
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.prepareYourPC",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.plugInBootableUSB",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.turnOnOrReboot",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.clickPermanent",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.clickAutomaticPartitioning",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.setAdminPassword",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.synchronize",
    "Onboarding.bmde.gettingStarted.steps.installBMDE.steps.setupWifiConfiguration",
];

export function checkIfStepIsClickable(stepIndex: number, lastCompletedStepIndex: number, hasIbr: boolean) {
    return (
        stepIndex <= lastCompletedStepIndex ||
        stepIndex === getActiveStep(lastCompletedStepIndex) ||
        lastCompletedStepIndex === (hasIbr ? LAST_COMPLETED_STEP_INDEX_WITH_IBR : LAST_COMPLETED_STEP_INDEX_WITHOUT_IBR)
    );
}

export function displayProgressBarProgress(lastCompletedStepIndex: number, previousProgress: number, hasIbr: boolean) {
    const firstIncrement = hasIbr ? 10 : 15;
    const otherIncrements = hasIbr ? 20 : 25;
    if (lastCompletedStepIndex === -1) {
        return previousProgress;
    } else if (lastCompletedStepIndex === 0) {
        return previousProgress + firstIncrement;
    } else {
        return previousProgress + otherIncrements;
    }
}

export function checkIfMenuExpanded(lastCompletedStepIndex: number, currentStepIndex: number, hasIbr: boolean) {
    if (
        lastCompletedStepIndex === -1 ||
        lastCompletedStepIndex === (hasIbr ? LAST_COMPLETED_STEP_INDEX_WITH_IBR : LAST_COMPLETED_STEP_INDEX_WITHOUT_IBR)
    ) {
        return false;
    }
    return (
        lastCompletedStepIndex !== -1 &&
        currentStepIndex > lastCompletedStepIndex &&
        isStepActive(lastCompletedStepIndex, currentStepIndex)
    );
}

export function getInitialProgressPercentage(
    stepIndex: number,
    hasIbr: boolean,
    canViewReports: boolean,
    products: string[]
) {
    let allSteps = products;

    if (products === BMDE_ONBOARDING_STEPS || products === BDE_ONBOARDING_STEPS || products === BMWE_ONBOARDING_STEPS) {
        if (!canViewReports) {
            allSteps = allSteps.filter((step) => step !== BmdeOnboardingSteps.STEP_VIEWING_REPORT);
        }
        if (!hasIbr) {
            allSteps = allSteps.filter((step) => step !== BmdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE);
        }
    }

    const increment = Math.ceil(100 / allSteps.length);
    const progressValues = [];
    for (let i = 0; i < allSteps.length; i++) {
        progressValues.push(Math.ceil(increment * (i + 1)));
    }
    const progressValue = progressValues[stepIndex] ?? increment;
    if (progressValue > 100) {
        return 100;
    }
    return progressValue;
}

export function getHomepageProductsMappingList(t: TFunction): ProductMappingItem[] {
    return [
        {
            productName: "Blancco Drive Eraser",
            description: t("Onboarding.home.products.blanccoDriveErasure"),
            onboarding: "BDE",
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco Drive Eraser for Mac",
            description: t("Onboarding.home.products.blanccoDriveErasureForMac"),
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco Drive Eraser for ARM",
            description: t("Onboarding.home.products.blanccoDriveErasureForArm"),
            deviceType: DeviceType.SERVER,
        },
        {
            productName: "Blancco Drive Verifier",
            description: t("Onboarding.home.products.blanccoDriveVerifier"),
            onboarding: "AUTOMATION",
            onboardingDescription: t("Onboarding.home.onboardingDescriptions.blanccoDriveVerifier"),
        },
        {
            productName: "Blancco Mobile Diagnostics and Erasure",
            description: t("Onboarding.home.products.blanccoMobileDiagnosticsAndErasure"),
            onboarding: "BMDE",
            deviceType: DeviceType.MOBILE,
        },
        {
            productName: "Blancco Eraser for Apple Devices",
            description: t("Onboarding.home.products.blanccoEraserForAppleDevices"),
            onboarding: "AUTOMATION",
            onboardingDescription: t("Onboarding.home.onboardingDescriptions.blanccoEraserForAppleDevices"),
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco Removable Media Eraser",
            description: t("Onboarding.home.products.blanccoRemovableMediaEraser"),
            deviceType: DeviceType.LOOSE_DRIVES,
        },
        {
            productName: "Blancco WinEraser",
            description: t("Onboarding.home.products.blanccoWinEraser"),
            onboarding: "AUTOMATION",
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco WinEraser for ARM",
            description: t("Onboarding.home.products.blanccoWinEraserForARM"),
            onboarding: "AUTOMATION",
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco LUN Eraser for Windows",
            description: t("Onboarding.home.products.blanccoLunEraserWin"),
            deviceType: DeviceType.SERVER,
        },
        {
            productName: "Blancco LUN Eraser for Unix",
            description: t("Onboarding.home.products.blanccoLunEraserUnix"),
            onboarding: "AUTOMATION",
            deviceType: DeviceType.SERVER,
        },
        {
            productName: "Blancco File Eraser",
            description: t("Onboarding.home.products.blanccoFileEraser"),
            deviceType: DeviceType.SERVER,
        },
        {
            productName: "Blancco Virtual Machine Eraser",
            description: t("Onboarding.home.products.blanccoVirtualMachineEraser"),
            deviceType: DeviceType.SERVER,
        },
        {
            productName: "Blancco WinEraser Volume Edition",
            description: t("Onboarding.home.products.blanccoWinEraser"),
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco WinEraser Enterprise Volume Edition",
            description: t("Onboarding.home.products.blanccoWinEraser"),
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco Management Portal On-Premise",
            description: t("Onboarding.home.products.blanccoManagementPortalOnPremise"),
            onboarding: "BMPOP",
        },
        {
            productName: "Blancco Asset Reimaging",
            description: t("Onboarding.home.products.blanccoAssetReimaging"),
            onboarding: "BAR",
            deviceType: DeviceType.LAPTOP,
        },
        {
            productName: "Blancco Mobile Workspace Edition",
            description: t("Onboarding.home.products.blanccoMobileWorkspaceEdition"),
            onboarding: "BMWE",
            deviceType: DeviceType.MOBILE,
        },
    ];
}

export function isOnLastStep(onboardingStep: string, onboardingProduct: string) {
    return onboardingStep === deduceLastAvailableStep(onboardingProduct);
}

export interface OnboardingState {
    bdeOnboarded: boolean;
    bmdeOnboarded: boolean;
    bmpOpOnboarded: boolean;
    barOnboarded: boolean;
    bmweOnboarded: boolean;
    lastCompletedBdeStep: string;
    lastCompletedBmdeStep: string;
    lastCompletedBmpOpStep: string;
    lastCompletedBarStep: string;
    lastCompletedBmweStep: string;
}

export const EMPTY_ONBOARDING_STATE: OnboardingState = {
    bdeOnboarded: false,
    bmdeOnboarded: false,
    bmpOpOnboarded: false,
    barOnboarded: false,
    bmweOnboarded: false,
    lastCompletedBdeStep: "",
    lastCompletedBmdeStep: "",
    lastCompletedBmpOpStep: "",
    lastCompletedBarStep: "",
    lastCompletedBmweStep: "",
};

export interface OnboardedProductStep {
    product: string;
    step: string;
}

export function buildOnboardingState(onboardedProducts: OnboardedProductStep[]): OnboardingState {
    const state = { ...EMPTY_ONBOARDING_STATE };
    onboardedProducts.forEach((onboardedProduct) => {
        if (onboardedProduct.product === "BDE") {
            state.lastCompletedBdeStep = onboardedProduct.step;
            state.bdeOnboarded = isOnLastStep(onboardedProduct.step, onboardedProduct.product);
        }
        if (onboardedProduct.product === "BMDE") {
            state.lastCompletedBmdeStep = onboardedProduct.step;
            state.bmdeOnboarded = isOnLastStep(onboardedProduct.step, onboardedProduct.product);
        }
        if (onboardedProduct.product === "BMPOP") {
            state.lastCompletedBmpOpStep = onboardedProduct.step;
            state.bmpOpOnboarded = isOnLastStep(onboardedProduct.step, onboardedProduct.product);
        }
        if (onboardedProduct.product === "BAR") {
            state.lastCompletedBarStep = onboardedProduct.step;
            state.barOnboarded = isOnLastStep(onboardedProduct.step, onboardedProduct.product);
        }
        if (onboardedProduct.product === "BMWE") {
            state.lastCompletedBmweStep = onboardedProduct.step;
            state.bmweOnboarded = isOnLastStep(onboardedProduct.step, onboardedProduct.product);
        }
    });
    return state;
}

function deduceLastAvailableStep(
    product: string
): BdeOnboardingSteps | BmdeOnboardingSteps | BmpOpOnboardingSteps | BarOnboardingSteps {
    if (product === OnboardingProductType.BAR) {
        return BarOnboardingSteps.STEP_VIEW_REPORT;
    }
    if (
        !tenantHasFeatureLicense("FEATURE_REPORTING") ||
        !userSessionService.userHasAllAuthorities([Authority.AUTH_REPORT_VIEW])
    ) {
        return product === OnboardingProductType.BDE || product === OnboardingProductType.BMWE
            ? BdeOnboardingSteps.STEP_RUNNING_ERASURE
            : product === OnboardingProductType.BAR
              ? BarOnboardingSteps.STEP_REIMAGE_TARGET_MACHINE
              : BmdeOnboardingSteps.STEP_RUNNING_ERASURE;
    }
    return product === OnboardingProductType.BDE || product === OnboardingProductType.BMWE
        ? BdeOnboardingSteps.STEP_VIEWING_REPORT
        : product === OnboardingProductType.BMDE
          ? BmdeOnboardingSteps.STEP_VIEWING_REPORT
          : product === OnboardingProductType.BAR
            ? BarOnboardingSteps.STEP_VIEW_REPORT
            : BmpOpOnboardingSteps.STEP_LAUNCHING_BMPOP;
}
